import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '../ui/Tooltip';
import { extractTaskKeys } from '../../services/git/commitTaskRefs';
import { knownTaskPrefixes, openTaskByKey } from '../../services/git/openTaskByKey';
import { useNotificationStore } from '../../store/notificationStore';
import '../boards/TaskKeys.css';

/** The prefixes of the user's boards, loaded once per minute (openTaskByKey.js). */
export function useKnownTaskPrefixes() {
  const [prefixes, setPrefixes] = useState(null);
  useEffect(() => {
    let alive = true;
    knownTaskPrefixes().then((set) => alive && setPrefixes(set));
    return () => {
      alive = false;
    };
  }, []);
  return prefixes;
}

/**
 * Task chips for a commit (commit ↔ task links, slice 1): every key in `text`
 * whose prefix is one of the user's boards ("APB-42"), each opening its task.
 * Nothing renders until the prefixes are known, or when no key matches.
 */
export default function CommitTaskChips({ text, prefixes, className = '' }) {
  const { t } = useTranslation();
  const keys = useMemo(() => (prefixes && prefixes.size ? extractTaskKeys(text, prefixes) : []), [text, prefixes]);
  if (!keys.length) return null;
  const open = async (key) => {
    const ok = await openTaskByKey(key).catch(() => false);
    if (!ok) useNotificationStore.getState().error(t('taskLinks.chips.notFound', { key }), t('taskLinks.chips.notFoundTitle'));
  };
  return (
    <span className={`commit-task-chips${className ? ` ${className}` : ''}`}>
      {keys.map((key) => (
        <Tooltip key={key} content={t('taskLinks.chips.open', { key })} side="top">
          <button type="button" className="commit-task-chip" data-task-key={key} onClick={() => open(key)}>
            {key}
          </button>
        </Tooltip>
      ))}
    </span>
  );
}
